import { useState } from 'react'
import { format, isToday, isYesterday, parseISO } from 'date-fns'
import { Plus, Search, Pencil, Scale, ClipboardList, Link2, ShieldOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useJournalEntries, useJournalLinkCountsAll, useCreateJournalEntry } from '@/hooks/useJournal'
import { cn } from '@/lib/utils'
import type { JournalEntry } from '@/types'

// ─── Type config ──────────────────────────────────────────────────────────────

const TYPE_CONFIG = {
  free:          { label: null,      accent: '',                              Icon: Pencil       },
  discernment:   { label: 'Discern', accent: 'border-l-2 border-indigo-400', Icon: Scale        },
  weekly_review: { label: 'Review',  accent: 'border-l-2 border-emerald-500', Icon: ClipboardList },
} satisfies Record<JournalEntry['type'], { label: string | null; accent: string; Icon: React.ElementType }>

function formatEntryDate(entry: JournalEntry): string {
  const d = parseISO(entry.entryDate)
  if (isToday(d)) return format(parseISO(entry.createdAt), 'h:mm a')
  if (isYesterday(d)) return 'Yesterday'
  if (d.getFullYear() === new Date().getFullYear()) return format(d, 'MMM d')
  return format(d, 'MMM d, yyyy')
}

// ─── List ─────────────────────────────────────────────────────────────────────

interface JournalListProps {
  selectedId: string | null
  onSelect: (id: string) => void
  /** Escalate the current query to the full-text search across notes + entries. */
  onDeepSearch?: (query: string) => void
}

const FILTER_OPTIONS: { value: JournalEntry['type'] | null; label: string }[] = [
  { value: null,           label: 'All'     },
  { value: 'free',         label: 'Free'    },
  { value: 'discernment',  label: 'Discern' },
  { value: 'weekly_review', label: 'Review' },
]

export function JournalList({ selectedId, onSelect, onDeepSearch }: JournalListProps) {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<JournalEntry['type'] | null>(null)
  const entries = useJournalEntries(search, typeFilter)
  const linkCounts = useJournalLinkCountsAll()
  const createEntry = useCreateJournalEntry()

  async function handleNew(type: JournalEntry['type'] = 'free') {
    const id = await createEntry.mutateAsync({ type })
    onSelect(id)
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-shrink-0">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search entries…"
            className="w-full pl-7 pr-3 py-2.5 md:py-1.5 text-sm bg-muted rounded-md border border-input focus:outline-none focus:ring-1 focus:ring-ring"
            aria-label="Search journal entries"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="flex-shrink-0 text-muted-foreground hover:text-foreground h-11 w-11 md:h-9 md:w-9 p-0"
              aria-label="New journal entry"
            >
              <Plus size={17} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => void handleNew('free')}>
              <Pencil size={13} className="mr-2 text-muted-foreground" />
              Free entry
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void handleNew('discernment')}>
              <Scale size={13} className="mr-2 text-indigo-500" />
              Discernment
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void handleNew('weekly_review')}>
              <ClipboardList size={13} className="mr-2 text-emerald-600" />
              Weekly review
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Type filter pills */}
      <div className="flex gap-1 mb-2 flex-shrink-0">
        {FILTER_OPTIONS.map((opt) => {
          const active = typeFilter === opt.value
          const Icon = opt.value ? TYPE_CONFIG[opt.value].Icon : null
          return (
            <button
              key={String(opt.value)}
              type="button"
              onClick={() => setTypeFilter(active ? null : opt.value)}
              className={cn(
                'flex items-center gap-1 px-2.5 py-1.5 md:px-2 md:py-0.5 rounded-full text-[11px] border transition-colors',
                active
                  ? opt.value === 'discernment'
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-950/40 dark:border-indigo-700 dark:text-indigo-300'
                    : opt.value === 'weekly_review'
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-secondary border-border text-foreground'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-ring',
              )}
              aria-pressed={active}
            >
              {Icon && <Icon size={9} />}
              {opt.label}
            </button>
          )
        })}
      </div>

      {/* Deep-search escalation — one search box, two depths */}
      {onDeepSearch && search.trim().length >= 2 && (
        <button
          type="button"
          onClick={() => onDeepSearch(search.trim())}
          className="flex-shrink-0 w-full text-left text-xs text-muted-foreground hover:text-foreground px-3 py-2 mb-1 rounded-md border border-dashed border-border hover:border-ring transition-colors"
        >
          Search everything for "{search.trim()}" → <span className="text-muted-foreground/60">(includes notes)</span>
        </button>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto space-y-0.5 min-h-0">
        {entries === undefined && (
          <p className="text-xs text-muted-foreground italic px-2 py-3">Loading…</p>
        )}
        {entries !== undefined && entries.length === 0 && (
          <p className="text-xs text-muted-foreground italic px-2 py-3">
            {search ? 'No entries match.' : 'No entries yet. Write your first one.'}
          </p>
        )}
        {entries?.map((entry) => (
          <EntryRow
            key={entry.id}
            entry={entry}
            selected={entry.id === selectedId}
            linkCount={linkCounts?.get(entry.id) ?? 0}
            onSelect={() => onSelect(entry.id)}
          />
        ))}
      </div>
    </div>
  )
}

// ─── Entry row ────────────────────────────────────────────────────────────────

function EntryRow({
  entry,
  selected,
  linkCount,
  onSelect,
}: {
  entry: JournalEntry
  selected: boolean
  linkCount: number
  onSelect: () => void
}) {
  const preview = entry.body.replace(/[#*`_>\-[\]]/g, '').trim()
  const config = TYPE_CONFIG[entry.type]

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full text-left px-3 py-3 md:py-2.5 rounded-lg transition-colors',
        config.accent,
        selected ? 'bg-secondary' : 'hover:bg-muted/60',
      )}
    >
      {/* Row 1: date + badges */}
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <span className="text-xs font-medium text-foreground tabular-nums">
          {formatEntryDate(entry)}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {config.label && (
            <span className={cn(
              'flex items-center gap-0.5 text-[10px] px-1 rounded',
              entry.type === 'discernment' ? 'text-indigo-500 bg-indigo-50 dark:bg-indigo-950/30'
                : 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30',
            )}>
              <config.Icon size={9} />
              {config.label}
            </span>
          )}
          {linkCount > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
              <Link2 size={9} />
              {linkCount}
            </span>
          )}
          {entry.aiExcludable && (
            <span title="Excluded from AI coaching" className="text-muted-foreground/50">
              <ShieldOff size={9} />
            </span>
          )}
        </div>
      </div>

      {/* Row 2: body preview */}
      <p className="text-xs text-muted-foreground truncate leading-relaxed">
        {preview || <span className="italic">Empty entry</span>}
      </p>
    </button>
  )
}
